/**
 * @file FooterTextSection.tsx
 * @description «Настройки сайта → Подвал» (phase19.16 Р1; SB17, SB18, SB86;
 * uiux-design-r1 §4): подпись подвала (шрифтом акцента, до 60 знаков),
 * копирайт с подстановкой года и «Сейчас: © 2026 …», страница «Условия
 * доставки и оплаты», на которую ведёт обязательная ссылка платформы.
 *
 * Приёмка Р2а §7.17 п. 3 (uiux-fonts-usage §4, §9 вопрос 8): под подписью —
 * подсказка о шрифте (`FOOTER_CAPTION_FONT_PARTS`): акцент, а если он не
 * выбран — «сейчас шрифт заголовков» (прежняя подсказка «Рукописным
 * шрифтом, если он выбран» была неверной).
 *
 * Приёмка Р2а (SB114): «Сейчас: …» считается как на витрине — общим
 * `formatFooterCopyright` с наименованием продавца из «Реквизитов»;
 * подсказка в пустом поле — шаблон по умолчанию, под полем — подстановки.
 */

import { FOOTER_CAPTION_FONT_PARTS, FOOTER_COPYRIGHT_DEFAULT_TEMPLATE } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { Combobox } from "../../ui/combobox";
import { Label } from "../../ui/label";
import { ElementFontNote } from "../../site-builder/section/elements/ElementFontNote";
import { TextField } from "../TextField";
import type { PartFieldsApi } from "../../../hooks/site-view/usePartFields";
import { useSellerInfo } from "../../../hooks/site-view/useSellerInfo";
import { sitePages, type SitePageListItem } from "../../../lib/api-site-pages";
import { copyrightPreview } from "../../../lib/site-view/brand-text";
import type { SiteBrandDraft, SitePageRef } from "../../../lib/site-view/types";

/** Выбранная страница списком «как в ответе сервера» (название и статус видны сразу). */
function pageRef(pages: SitePageRef[] | null, id: string | null): SitePageRef | null {
  const page = id ? pages?.find((p) => p.id === id) : undefined;
  return page ? { id: page.id, title: page.title, slug: page.slug, isPublished: page.isPublished } : null;
}

export function FooterTextSection({ brand }: { brand: PartFieldsApi<SiteBrandDraft> }) {
  const { fields, set } = brand;
  const [pages, setPages] = useState<SitePageListItem[] | null>(null);
  // Правообладатель для «{legalName}» — основной профиль «Реквизитов» (нет — название сайта).
  const legalName = useSellerInfo(fields.shopContactEmail).info?.profile?.legalName?.trim() || null;

  useEffect(() => {
    sitePages
      .list()
      .then(setPages)
      .catch(() => setPages([]));
  }, []);

  const year = new Date().getFullYear();
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <TextField label="Подпись подвала" value={fields.footerSignature} max={60} onChange={(v) => set("footerSignature", v)} />
        <ElementFontNote parts={FOOTER_CAPTION_FONT_PARTS} backLabel="Подпись подвала" back={{ panel: "settings", sub: "footer" }} />
      </div>
      <div className="flex flex-col gap-1">
        <TextField
          label="Копирайт"
          value={fields.copyrightText}
          max={120}
          placeholder={FOOTER_COPYRIGHT_DEFAULT_TEMPLATE}
          hint={`Сейчас: ${copyrightPreview(fields.copyrightText, fields.siteName, year, legalName)}`}
          onChange={(v) => set("copyrightText", v)}
        />
        <ul aria-label="Подстановки копирайта" className="type-caption flex flex-col text-on-surface-variant">
          <li>{"{year}"} — текущий год (годы «с … по сейчас»: «© 2024–{"{year}"} …»)</li>
          <li>{"{legalName}"} — наименование продавца из «Реквизитов»</li>
          <li>{"{siteName}"} — название сайта</li>
        </ul>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="delivery-terms-page">Страница «Условия доставки и оплаты»</Label>
        <Combobox
          id="delivery-terms-page"
          options={(pages ?? (fields.deliveryTermsPage ? [fields.deliveryTermsPage] : [])).map((p) => ({
            value: p.id,
            label: p.title,
            description: `/${p.slug} · ${p.isPublished ? "опубликована" : "не опубликована"}`,
          }))}
          value={fields.deliveryTermsPage?.id ?? null}
          placeholder={pages === null ? "Загружаем страницы…" : "Не выбрана"}
          emptyText="Страниц нет — создайте её в «Страницах»"
          onChange={(id) => set("deliveryTermsPage", pageRef(pages, id))}
        />
        {fields.deliveryTermsPage && !fields.deliveryTermsPage.isPublished ? (
          <p className="type-caption text-warning">! Страница не опубликована — ссылки в подвале не будет, пока её не опубликуют.</p>
        ) : null}
        <p className="type-caption text-on-surface-variant">
          На неё ведёт обязательная ссылка в подвале рядом с офертой и политикой. Не выбрана — ссылки не будет.
        </p>
      </div>
    </div>
  );
}
